import React, { useState } from 'react';
import axios from 'axios';
import '../styles/bookAppointmentPage.css';
import useScrollAnimation from '../hooks/useScrollAnimation';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { PHARMACY_NUMBER } from '../constants/constant';
import { formatPhoneNumber } from '../utils/phoneFormatter';

const API_BASE = process.env.REACT_APP_API_URL;

function BookAppointment() {
    useScrollAnimation();

    const initialFormData = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        reason: '',
    });

    const [formData, setFormData] = useState(initialFormData);
    const [timeSlots, setTimeSlots] = useState([{ date: '', time: '' }]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleTimeSlotChange = (index, field, value) => {
        const updatedSlots = [...timeSlots];
        updatedSlots[index][field] = value;
        setTimeSlots(updatedSlots);
    };

    function generateHalfHourSlots(start = "09:15", end = "17:45") {
        const slots = [];
        let [startH, startM] = start.split(":").map(Number);
        let [endH, endM] = end.split(":").map(Number);

        let totalStartMins = startH * 60 + startM;
        const totalEndMins = endH * 60 + endM;

        while (totalStartMins <= totalEndMins) {
            const hrs = Math.floor(totalStartMins / 60);
            const mins = totalStartMins % 60;
            const timeString = `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
            slots.push(timeString);
            totalStartMins += 30;
        }
        return slots;
    }


    const addTimeSlot = () => {
        setTimeSlots([...timeSlots, { date: '', time: '' }]);
    };

    const removeTimeSlot = (index) => {
        const updatedSlots = [...timeSlots];
        updatedSlots.splice(index, 1);
        setTimeSlots(updatedSlots);
    };

    const resetForm = () => {
        setFormData(initialFormData);
        setTimeSlots([{ date: '', time: '' }]);
        setSubmitStatus(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);
        // console.log('Form Data:', formData);
        // console.log('Time Slots:', timeSlots);
        try {
            // await axios.post('http://localhost:5001/api/send-appointment', {
            //     ...formData,
            //     timeSlots
            // });
            await axios.post(`${API_BASE}api/send-appointment`, {
                ...formData,
                timeSlots
            });

            setSubmitStatus({
                type: 'success',
                message: 'Appointment request submitted successfully! We will contact you shortly to confirm your appointment.'
            });

            setTimeout(() => {
                resetForm();
            }, 2000);

        } catch (err) {
            console.error(err);
            setSubmitStatus({
                type: 'error',
                message: `Failed to submit appointment request. Please try again or call us directly at ${formatPhoneNumber(PHARMACY_NUMBER)}.`
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
        <Navbar />
        <section className="ba-wrapper">
        {isSubmitting && (
                <div className="ba-loading-overlay">
                    <div className="ba-spinner-container">
                        <div className="ba-spinner"></div>
                        <p>Submitting your appointment request...</p>
                    </div>
                </div>
            )}
            
            <div className="ba-intro slide-down">
                <h1 className="ba-title fade-in"><i className="fas fa-calendar-check"></i> Book Your Appointment</h1>
                <p className="fade-in-delay">Need a consultation, prescription refill, or vaccination?</p>
                <p className="fade-in-delay-2">Booking an appointment with Mirror Lake Pharmacy is quick and easy:</p>
                <div className="ba-steps fade-in">
                    <div className="ba-step"><span>1</span> Fill in your personal and contact details.</div>
                    <div className="ba-step"><span>2</span> Choose a reason for your visit.</div>
                    <div className="ba-step"><span>3</span> Select one or more preferred dates and times.</div>
                    <div className="ba-step"><span>4</span> Submit your request — we’ll confirm shortly!</div>
                </div>
                <p className="ba-note fade-in">* All fields are required unless otherwise noted.</p>
            </div>

            {submitStatus && (
                <div className={`ba-status-message ${submitStatus.type === 'success' ? 'ba-status-success' : 'ba-status-error'}`}>
                    <i className={`fas ${submitStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                    <span>{submitStatus.message}</span>
                </div>
            )}

            <div className="ba-card slide-up">
                <form className="ba-form fade-in-delay-3" onSubmit={handleSubmit}>
                    <div className="ba-form-row">
                        <input type="text" name="firstName" placeholder="First Name*" required onChange={handleChange} value={formData.firstName} />
                        <input type="text" name="lastName" placeholder="Last Name*" required onChange={handleChange} value={formData.lastName} />
                    </div>

                    <div className="ba-form-row">
                        <input type="email" name="email" placeholder="Email*" required onChange={handleChange} value={formData.email} />
                        <input type="text" name="phone" placeholder="Phone Number*" required onChange={handleChange} value={formData.phone} />
                    </div>

                    <div className="ba-form-row">
                        <select name="reason" required onChange={handleChange} value={formData.reason}>
                            <option value="">Select Reason*</option>
                            <option value="Travel Consultation">Travel Consultation</option>
                            <option value="Pharmacist Walk-in Clinic Apointment">Pharmacist Walk-in Clinic Appointment</option>
                            <option value="Prescription Refills">Prescription Refills</option>
                            <option value="Smoking Cessation Consultation">Smoking Cessation Consultation</option>
                            <option value="Medication Review with Pharmacist">Medication Review with Pharmacist</option>
                            <option value="Vaccination ( Flu, Pneumonia, Tetanus and RSV Vaccine)
">Vaccination ( Flu, Pneumonia, Tetanus and RSV Vaccine)
                            </option>
                            <option value="Injection Service Appointment">Injection Service Appointment</option>
                        </select>
                    </div>

                    <h3 className="ba-section-heading">Preferred Appointment Time(s)</h3>
                    {timeSlots.map((slot, index) => (
                        <div className="ba-form-row" key={index}>
                            <input type="date" required value={slot.date} onChange={(e) => handleTimeSlotChange(index, 'date', e.target.value)} />
                            {/* <input type="time" step="1800" required value={slot.time} onChange={(e) => handleTimeSlotChange(index, 'time', e.target.value)} /> */}
                            <select
                                required
                                value={slot.time}
                                onChange={(e) => handleTimeSlotChange(index, 'time', e.target.value)}
                            >
                                <option value="">Select Time</option>
                                {generateHalfHourSlots().map((time) => (
                                    <option key={time} value={time}>{time}</option>
                                ))}
                            </select>

                            {index > 0 && (
                                <button type="button" className="remove-slot" onClick={() => removeTimeSlot(index)}>Remove</button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={addTimeSlot} className="add-slot">+ Add Another Time Slot</button>

                    <button type="submit" className="submit-btn">Submit Appointment Request</button>
                </form>
            </div>
        </section>
        <Footer/>
    </>
    );
}

export default BookAppointment;
